import { Flex } from "@mantine/core";
import { useMemo, useState } from "react";
import {
  FormProvider,
  type SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import FgasGwpList from "src/components/pages/Settings/YachtSetUp/Fgas/FgasGwpList";
import FgasLegalStatusForm from "src/components/pages/Settings/YachtSetUp/Fgas/FgasLegalStatusForm";
import YachtSetupSectionHeader from "src/components/pages/Settings/YachtSetUp/YachtSetupSectionHeader";
import Loader from "src/components/shared/Loader/Loader";
import { useGetApi, usePostApi } from "src/hooks";
import { PageContainer } from "src/styled";
import { getYachtId } from "src/utils";
import { Notification } from "src/utils/Notification";

import { type IFgasForm, type IFgasGwpItem } from "./Fgas.types";
import {
  emptyGwpItem,
  mapGwp,
  mapLegalStatus,
  toLegalStatusPayload,
} from "./Fgas.utils";

const Fgas = () => {
  const {
    data: legalStatus,
    isLoading: isLegalStatusLoading,
    refetch: refetchLegalStatus,
  } = useGetApi({
    request: "get-fgas-legal-status",
    mapper: mapLegalStatus,
    cache: false,
  });
  const {
    data: gwp,
    isLoading: isGwpLoading,
    refetch: refetchGwp,
  } = useGetApi({
    request: "get-fgas-gwp",
    mapper: mapGwp,
    cache: false,
  });

  const { post } = usePostApi();
  const [isSaving, setIsSaving] = useState(false);
  const [removedGwp, setRemovedGwp] = useState<IFgasGwpItem[]>([]);

  const values = useMemo(
    () => (legalStatus && gwp ? { legalStatus, gwp: gwp.active } : undefined),
    [legalStatus, gwp],
  );

  const methods = useForm<IFgasForm>({ values });
  const { dirtyFields } = methods.formState;

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "gwp",
  });

  const handleRemove = (index: number) => {
    const item = methods.getValues(`gwp.${index}`);
    if (item.id) setRemovedGwp(prev => [...prev, { ...item, active: 0 }]);
    remove(index);
  };

  const onSubmit: SubmitHandler<IFgasForm> = async data => {
    const yachtid = getYachtId();
    const requests: Promise<any>[] = [];

    if (dirtyFields.legalStatus) {
      requests.push(
        post({
          request: "save-fgas-legal-status",
          body: {
            params: { yachtid, list: [toLegalStatusPayload(data.legalStatus)] },
          },
        }),
      );
    }

    if (dirtyFields.gwp || removedGwp.length > 0) {
      const list = [...data.gwp, ...removedGwp, ...(gwp?.archived ?? [])];
      requests.push(
        post({
          request: "save-fgas-gwp",
          body: { params: { yachtid, list } },
        }),
      );
    }

    if (requests.length === 0) return;

    setIsSaving(true);
    const results = await Promise.all(requests);
    setIsSaving(false);
    if (results.some(res => !res)) return;

    setRemovedGwp([]);
    methods.reset(data);
    await Promise.all([refetchLegalStatus(), refetchGwp()]);
    Notification("Success", "F-GAS has been successfully changed.", "green");
  };

  const isLoading = !values || isLegalStatusLoading || isGwpLoading;

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}>
          <YachtSetupSectionHeader
            customLinks={links => [
              ...links.slice(0, -1),
              { title: "F-GAS", href: "" },
            ]}
            isSaving={isSaving}
            section="fgas"
            showHistory={false}
          />
          {isLoading ? (
            <Loader />
          ) : (
            <Flex
              w="100%"
              mt="md"
              mih={{ lg: 0 }}
              flex={{ base: "none", lg: 1 }}
              gap="xl"
              align="flex-start"
              direction={{ base: "column", lg: "row" }}>
              <FgasLegalStatusForm />
              <FgasGwpList
                fields={fields}
                onAdd={() => append(emptyGwpItem())}
                onRemove={handleRemove}
              />
            </Flex>
          )}
        </form>
      </FormProvider>
    </PageContainer>
  );
};

export default Fgas;
